import { useEffect, useRef, useSyncExternalStore } from "react";
import { CircleAlert, X } from "lucide-react";
import { dismissError, errorNotices, reportError } from "./report";
import "./styles.css";

export function ErrorToasts() {
  const host = useRef<HTMLElement>(null);
  const notices = useSyncExternalStore(
    errorNotices.subscribe,
    errorNotices.getSnapshot,
  );
  useEffect(() => {
    // The top layer keeps notifications visible above native consent dialogs.
    host.current?.hidePopover?.();
    if (notices.length) host.current?.showPopover?.();
  }, [notices]);
  useEffect(() => {
    // Error boundaries do not catch event handlers, timers, or rejected promises.
    const error = (event: ErrorEvent) => {
      if (event.message) reportError(event.error ?? event.message);
    };
    const rejection = (event: PromiseRejectionEvent) =>
      reportError(event.reason);
    window.addEventListener("error", error);
    window.addEventListener("unhandledrejection", rejection);
    return () => {
      window.removeEventListener("error", error);
      window.removeEventListener("unhandledrejection", rejection);
    };
  }, []);
  return (
    <aside
      ref={host}
      popover="manual"
      className="error-toasts"
      aria-label="Error notifications"
    >
      {notices.map((notice) => (
        <div className="error-toast" role="alert" key={notice.id}>
          <CircleAlert size={18} aria-hidden="true" />
          <div>
            <strong>Something went wrong</strong>
            <p>{notice.message}</p>
          </div>
          <button
            aria-label="Dismiss error"
            onClick={() => dismissError(notice.id)}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      ))}
    </aside>
  );
}
